import { TimeFunction } from "@/common/datetime/TimeFunction";
import { useComponentDimensions } from "@/hooks/useWindowDimensions";
import moment from "moment";
import { useEffect, useState } from "react";
import { Layer, Line, Stage, Text } from "react-konva";
import { ITimeSliderCanvasProps } from "./Models";
import { TimeRangeSliderWrapper } from "./Styles";
import { renderColorByTheme } from "@/common/function/FunctionTheme";
import { BuildRCAttribute } from "@/common/function/RCAttribute";

export const TimeRangeSliderCanvas = ({
  startTimeSelected,
  format,
  formatHour,
  onChange,
  theme,
  rcName,
}: ITimeSliderCanvasProps) => {
  const [canvasSection_ref, width] = useComponentDimensions();
  const height_default = 24;
  const [widthState, setWidthState] = useState<number>(0);
  const [sliderTime, setSliderTime] = useState<Date | null>(null);

  useEffect(() => {
    if (width > 0) {
      setWidthState(width);
      getCoordinates(width);
    }
  }, [width]);

  const [lstHours, setLstHours] = useState<any[]>([]);
  const [dateStart, setDateStart] = useState<any>(null);
  const [dateDefault, setDateDefault] = useState<moment.Moment | null>(null);

  function splitArray(timeInput: string): string[] {
    const formatHourDef = "HH:mm";
    //if (formatHour) formatHourDef = formatHour;
    const lstHours = [];
    for (let i = 0; i < 24; i++) {
      lstHours.push(moment({ hour: i }).format(formatHourDef));
    }

    let lstMap: string[] = [];
    const threeArrays: string[][] = [
      lstHours.slice(0, 8),
      lstHours.slice(8, 16),
      lstHours.slice(16),
    ];
    threeArrays?.some((elements: string[], i: number) => {
      const foundIndex = elements?.findIndex(
        (element: string) => element.slice(0, 2) === timeInput
      );
      if (foundIndex !== -1) {
        lstMap = elements.concat(
          ...threeArrays[(i + 1) % 3],
          ...threeArrays[(i + 2) % 3]
        );
        return true;
      }
      return false;
    });
    return lstMap;
  }

  const handleSplitArray = (timeInput: string) => {
    setLstHours(splitArray(timeInput));
  };

  const [_listCoordinates, setListCoordinates] = useState([]);

  useEffect(() => {
    if (
      sliderTime === null ||
      sliderTime.getHours() != startTimeSelected?.getHours() ||
      sliderTime.getMinutes() != startTimeSelected?.getMinutes() ||
      sliderTime.getDate() != startTimeSelected?.getDate() ||
      sliderTime.getMonth() != startTimeSelected?.getMonth() ||
      sliderTime.getFullYear() != startTimeSelected?.getFullYear()
    ) {
      const hourSelected = startTimeSelected?.getHours();
      const hourSelectedString = hourSelected?.toString();
      const timeInput =
        hourSelectedString && hourSelectedString?.length > 1
          ? hourSelectedString
          : "0" + (hourSelectedString || "08");
      handleSplitArray(timeInput);
      const _date = moment(startTimeSelected);
      setDateDefault(_date);
      setDateStart(startTimeSelected);
      setSliderTime(startTimeSelected || new Date());
    }
  }, [startTimeSelected]);

  useEffect(() => {
    lineY();
  }, [_listCoordinates]);

  const lineY = () => {
    return _listCoordinates.map((item: any, index: number) => {
      return (
        <Line key={index} points={item} stroke="#BDBDBD" strokeWidth={1.5} />
      );
    });
  };

  function setHourFromString(date: Date, hourString: string) {
    try {
      if (!date || !hourString) {
        console.error("Invalid date or hourString");
        return null; // or throw an error
      }

      const [hours, minutes] = hourString.split(":");

      if (isNaN(Number(hours)) || isNaN(Number(minutes))) {
        console.error("Invalid hours or minutes");
        return null; // or throw an error
      }

      date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
      return date;
    } catch (error) {
      console.error("Error in setHourFromString:", error);
      return null; // or throw an error
    }
  }

  function getCoordinates(width: any) {
    // const _width = (state_ref.current as unknown as any)?.width() || width;
    const widthCanvas = width;
    const _tileSize = widthCanvas / 19;
    const _halfTileSize = _tileSize / 2;
    let _lastTileSize = _halfTileSize + 5;

    const listCoordinates: any = [];

    for (let index = 0; index < 19; index++) {
      listCoordinates.push([
        _lastTileSize,
        height_default,
        _lastTileSize,
        height_default - 6,
      ]);
      _lastTileSize = _lastTileSize + _tileSize - 0.5;
    }
    setListCoordinates(listCoordinates);
  }

  const drawHours = () => {
    let position = -1;
    const scale = widthState / window.innerWidth;
    const fonSize = 14 * scale;

    return _listCoordinates.map((item, index) => {
      if (index >= 2 && index % 2 === 0 && index <= 16) {
        position += 1;
        const time_ = lstHours[position];
        const dateCvt = setHourFromString(new Date(dateStart), time_);
        const time = TimeFunction.dateTimeFormatter(
          dateCvt || new Date(),
          formatHour
        ) as string;
        const trimmedTimeString =
          time.length > 5 ? time.replace(":00 ", "") : time;
        const fontSize = time.length > 5 ? fonSize - 4 : fonSize - 3;
        return (
          <Text
            fill={renderColorByTheme(theme)}
            data-qa="d"
            key={index}
            x={Math.round(item[0] - fonSize)}
            y={Math.round(13)}
            text={trimmedTimeString}
            fontSize={fontSize}
            perfectDrawEnabled={false}
          />
        );
      }
    });
  };

  const drawPrevHours = () => {
    const scale = widthState / window.innerWidth;
    const fonSize = 14 * scale;

    return _listCoordinates.map((item: any, index: number) => {
      if (index === 0) {
        const time_ = lstHours[23];
        const dateCvt = setHourFromString(new Date(dateStart), time_);
        const time = TimeFunction.dateTimeFormatter(
          dateCvt || new Date(),
          formatHour
        ) as string;
        const trimmedTimeString =
          time.length > 5 ? time.replace(":00 ", "") : time;
        const fontSize = time.length > 5 ? fonSize - 4 : fonSize - 3;
        return (
          <Text
            data-qa="d"
            key={index}
            x={Math.round(item[0] - fonSize)}
            y={Math.round(13)}
            fill={renderColorByTheme(theme)}
            text={trimmedTimeString}
            fontSize={fontSize}
            perfectDrawEnabled={false}
          />
        );
      }
    });
  };

  const drawNextHours = () => {
    const scale = widthState / window.innerWidth;
    const fonSize = 14 * scale;
    return _listCoordinates.map((item: any, index: number) => {
      if (index === 18) {
        const time_ = lstHours[8];
        const dateCvt = setHourFromString(new Date(dateStart), time_);
        const time = TimeFunction.dateTimeFormatter(
          dateCvt || new Date(),
          formatHour
        ) as string;
        const trimmedTimeString =
          time.length > 5 ? time.replace(":00 ", "") : time;
        const fontSize = time.length > 5 ? fonSize - 4 : fonSize - 3;
        return (
          <Text
            data-qa="d"
            key={index}
            x={Math.round(item[0] - fonSize)}
            y={Math.round(13)}
            fill={renderColorByTheme(theme)}
            text={trimmedTimeString}
            fontSize={fontSize}
            perfectDrawEnabled={false}
          />
        );
      }
    });
  };

  function rotateArray(arr: any[], rotation: number): any {
    return arr
      .slice(arr.length - rotation)
      .concat(arr.slice(0, arr.length - rotation));
  }

  function controlDate(type: string): void {
    const sign = type === "up" ? 1 : -1;
    const _date = dateDefault && dateDefault?.add(sign, "days");
    setDateDefault(_date);
    setDateStart(startTimeSelected);
  }

  const controlTime = (direction: string) => {
    const offset = direction === "prev" ? 8 : 16;
    const rotatedArray = rotateArray([...lstHours], offset);
    setLstHours(rotatedArray);
    const foundIndex =
      direction === "prev"
        ? rotatedArray?.slice(0, 8).lastIndexOf("23:00")
        : rotatedArray?.slice(0, 8).indexOf("00:00");
    //console.log(rotatedArray, foundIndex, "rotatedArray");
    if (foundIndex !== -1) {
      controlDate(direction === "prev" ? "down" : "up");
    }
  };

  const prevFunc = () => controlTime("prev");
  const nextFunc = () => controlTime("next");

  useEffect(() => {
    if (dateDefault) {
      onChangeTime();
    }
  }, [lstHours]);

  const onChangeSlider = (timeSlider: Date) => {
    if (onChange) {
      onChange(timeSlider);
    }
  };

  const onChangeTime = () => {
    const _date =
      dateDefault &&
      dateDefault.set({
        hour: lstHours.length ? lstHours[0].slice(0, 2) : "08:00",
        minute: 0,
        second: 0,
        millisecond: 0,
      });

    onChangeSlider(_date && _date?.toDate() ? _date?.toDate() : new Date());
  };

  return (
    <TimeRangeSliderWrapper
      className="TimeRangeSliderWrapper time-line-block"
      $theme={theme}
    >
      <div className="content-time-line">
        <div
          className="button-left"
          onClick={() => prevFunc()}
          {...BuildRCAttribute(`time_range_slider.btn_pre.${rcName}`)}
        >
          <svg
            className="left-arrow"
            width="5"
            height="10"
            viewBox="0 0 5 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0 5L5 0V10L0 5Z" fill="#BDBDBD" />
          </svg>
        </div>
        <div
          className="button-right"
          onClick={() => nextFunc()}
          {...BuildRCAttribute(`time_range_slider.btn_next.${rcName}`)}
        >
          <svg
            className="right-arrow"
            width="5"
            height="10"
            viewBox="0 0 5 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M5 5L0 0L0 10L5 5Z" fill="#BDBDBD" />
          </svg>
        </div>
        <div>
          <div ref={canvasSection_ref} id="canvas-section">
            <Stage width={widthState} height={height_default} clearBeforeDraw>
              <Layer>
                {drawPrevHours()}
                {drawHours()}
                {drawNextHours()}
              </Layer>
            </Stage>
          </div>
        </div>
      </div>
      <div
        className="date-time"
        style={{ fontSize: 11 }}
        {...BuildRCAttribute(`time_range_slider.date_time.${rcName}`)}
      >
        {
          TimeFunction.dateTimeFormatter(
            dateStart ?? new Date(),
            format
          ) as string
        }
      </div>
    </TimeRangeSliderWrapper>
  );
};
